"use client";

import { useBoard } from "@/components/BoardProvider";
import { DISCORD_INVITE_URL, WHATSAPP_INVITE_URL } from "@/lib/community";
import { cn } from "@/lib/utils";

export type CommunityPlatform = "discord" | "whatsapp";

function DiscordLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M20.317 4.3698a19.7913 19.7913 0 0 0-4.8851-1.5152.0741.0741 0 0 0-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 0 0-.0785-.037 19.7363 19.7363 0 0 0-4.8852 1.515.0699.0699 0 0 0-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 0 0 .0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 0 0 .0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 0 0-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 0 1-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 0 1 .0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 0 1 .0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 0 1-.0066.1276 12.2986 12.2986 0 0 1-1.873.8914.0766.0766 0 0 0-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 0 0 .0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 0 0 .0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 0 0-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
  );
}

function WhatsappLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.358.101 11.892c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a11.882 11.882 0 0 0 5.71 1.454h.006c6.585 0 11.946-5.359 11.949-11.945a11.821 11.821 0 0 0-3.487-8.463" />
    </svg>
  );
}

function PlatformCard({
  href,
  name,
  description,
  logo,
  brandClassName,
  onJoin,
}: {
  href: string;
  name: string;
  description: string;
  logo: React.ReactNode;
  brandClassName: string;
  onJoin?: () => void;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onJoin}
      className="group flex flex-col items-center gap-4 rounded-3xl border-2 border-border bg-card px-6 py-8 text-center transition-all duration-150 hover:border-primary/50 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <span
        className={cn(
          "grid size-20 shrink-0 place-items-center rounded-2xl text-white transition-transform duration-150 group-hover:scale-105",
          brandClassName,
        )}
      >
        <span className="size-11">{logo}</span>
      </span>
      <span className="space-y-1">
        <span className="block font-polysans text-lg font-semibold">{name}</span>
        <span className="block font-polysans text-sm text-muted-foreground">{description}</span>
      </span>
      <span className="mt-1 inline-flex h-11 w-full items-center justify-center rounded-full bg-primary px-6 font-polysans text-sm font-semibold text-primary-foreground transition-opacity group-hover:opacity-90">
        Join {name}
      </span>
    </a>
  );
}

/**
 * The community pitch (heading + WhatsApp/Discord cards), shared by the global
 * one-time modal and the final onboarding step so both stay identical.
 */
export function CommunityLinks({
  onJoin,
  className,
}: {
  onJoin?: (platform: CommunityPlatform) => void;
  className?: string;
}) {
  const board = useBoard();
  const exam = board.shortName;
  return (
    <div className={cn("space-y-6", className)}>
      <div className="text-center">
        <h2 className="font-script text-4xl leading-tight sm:text-5xl">
          Join the {exam} community
        </h2>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <PlatformCard
          href={WHATSAPP_INVITE_URL}
          name="WhatsApp"
          description="Quick questions, daily chat"
          logo={<WhatsappLogo className="size-full" />}
          brandClassName="bg-[#25D366]"
          onJoin={onJoin ? () => onJoin("whatsapp") : undefined}
        />
        <PlatformCard
          href={DISCORD_INVITE_URL}
          name="Discord"
          description="Past papers, help channels"
          logo={<DiscordLogo className="size-full" />}
          brandClassName="bg-[#5865F2]"
          onJoin={onJoin ? () => onJoin("discord") : undefined}
        />
      </div>
    </div>
  );
}
